import React, { useState } from 'react';
import ExpandSection from 'src/components/ExpandSection';
import { QKeys } from 'src/api/types';
import Button from 'src/components/form/Button';
import { LinkIncidentsToScenarioModal } from 'src/components/modals';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import useVersion from 'src/hooks/useVersion';
import { NoData, Spacer, TableSkeleton } from 'src/common';
import GenericTable, { TableSheetIncident } from 'src/components/GenericTable';
import { TableIds } from 'src/api/types/misc';
import { queryCache } from 'react-query';
import { useColumnsIncidents } from './useColumnsIncidents';
import { useScenarioIncidentsQuery, useUnlinkIncidentFromScenariosMutation } from 'src/graphql/_generated';
import { isQueryLoading, useCache } from 'src/graphql';
import { showSuccessToast, toastMessages } from 'src/components/Toast';

interface IncidentsSectionProps {
  scenarioId: string;
  scenarioName: string;
  versionId?: string;
}

export const IncidentsSection: React.FC<IncidentsSectionProps> = ({ scenarioId, scenarioName, versionId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { versionId: hookVersionId } = useVersion();
  const { refetchQuery } = useCache();

  const handleOpen = (open: boolean) => {
    setIsOpen(open);
    if (open) setHasOpened(true);
  };

  const {
    data: incData,
    loading,
    networkStatus,
  } = useScenarioIncidentsQuery({ variables: { id: scenarioId, versionId }, skip: !hasOpened });

  const isLoading = isQueryLoading({ loading, data: incData?.scenario, networkStatus });
  const incidents = incData?.scenario?.incidents || [];

  const [unlink] = useUnlinkIncidentFromScenariosMutation();

  const { columns, GTColumnSelectAnchorExported } = useColumnsIncidents({
    data: incidents,
    tdm: {
      onUnlinkIncidentClick: async (incidentId) => {
        await unlink({
          variables: {
            incidentId,
            scenarioIds: [scenarioId],
          },
        });
        queryCache.invalidateQueries([QKeys.Scenario, { scenarioId, variant: 'base' }]);
        queryCache.invalidateQueries([QKeys.Scenario, { scenarioId }]);
        await refetchQuery({ include: ['scenario', 'scenarioIncidents'] });
        showSuccessToast(toastMessages.incident.unlinked);
      },
    },
  });

  return (
    <>
      {isModalOpen && (
        <LinkIncidentsToScenarioModal
          onClose={() => setIsModalOpen(false)}
          invalidateQueries={['scenarioIncidents']}
          scenarioId={scenarioId}
          subTitle={{
            title: 'SCENARIO',
            value: scenarioName,
          }}
        />
      )}
      <ExpandSection
        title="INCIDENTS"
        sectionId="scenario_incidents"
        onChange={handleOpen}
        endComponent={
          isOpen && !hookVersionId ? (
            <Button
              primary
              onClick={() => {
                setIsModalOpen(true);
                mpEvent(MPEvents.ButtonClick, {
                  button: 'Add incidents',
                  tags: ['SCENARIO', 'INCIDENT'],
                });
              }}
              data-cy="add-incidents-btn"
              $constWidth={200}
            >
              + ADD INCIDENTS
            </Button>
          ) : undefined
        }
      >
        {isLoading ? (
          <TableSkeleton />
        ) : incidents.length ? (
          <>
            <Spacer $px={20} />
            <GenericTable
              tableId={TableIds.scenarioIncidents}
              data={incidents}
              columns={columns}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
              expandContent={(data) => (
                <TableSheetIncident
                  data={{
                    id: data.id,
                    name: data.name,
                    source: data.source,
                    dateOfIncident: data.dateOfIncident,
                    description: data.description,
                    ref: data.ref,
                    scenarios: [],
                    financialImpactLower: data.financialImpactLower,
                    financialImpactUpper: data.financialImpactUpper,
                    impact: data.impact,
                    incidentType: data.incidentType,
                    owner: data.owner,
                    severity: data.severity,
                  }}
                />
              )}
              searchable={['ref', 'name', 'incidentType', 'dateOfIncident']}
            />
          </>
        ) : (
          <>
            <Spacer $px={5} />
            <NoData title="No incidents linked" />
          </>
        )}
      </ExpandSection>
    </>
  );
};
